import { type FC, useMemo } from 'react'
import {
  useRefinementList,
  type UseRefinementListProps,
} from 'react-instantsearch'
import { useIntl } from 'react-intl'

import { productSearchGetRefinementLabel } from '../productSearchGetRefinementLabel'
import {
  ProductSearchRefinement,
  ProductSearchRefinementContext,
  type ProductSearchRefinementProps,
} from '../ProductSearchRefinement'
import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementCheckboxListbox } from './ProductSearchRefinementCheckboxListbox'
import { ProductSearchRefinementCheckboxOption } from './ProductSearchRefinementCheckboxOption'

type Item = ReturnType<typeof useRefinementList>['items'][number]

/**
 * Identify items between Algolia refinements updates.
 * Compare a unique `value` property.
 */
const areOptionsEqual = (itemA: Item, itemB: Item): boolean =>
  itemA.value === itemB.value

/**
 * The `refine` from Algolia works like a toggle. Check if we should toggle item from the list.
 */
const shouldRefine = (item: Item, selectedItems: Item[]): boolean =>
  // if items are selected and not refined
  (selectedItems.includes(item) && !item.isRefined) ||
  // if the item isn't selected but was refined before
  (!selectedItems.includes(item) && item.isRefined)

export type ProductSearchRefinementCheckboxProps = Omit<
  ProductSearchRefinementProps<Item, true>,
  'hidden'
> &
  Pick<UseRefinementListProps, 'attribute' | 'transformItems'> & {
    /**
     * Use `hidden` to physically remove the component from the DOM, but keep
     * the hook execution to preserve UI state of Algolia search.
     */
    hidden: boolean
  }

const PRODUCT_SEARCH_REFINEMENT_CHECKBOX_FACET_LIMIT = 10_000
const PRODUCT_SEARCH_REFINEMENT_CHECKBOX_REDUCED_FACET_LIMIT = 5

const DEFAULT_SORT_BY = ['name:asc' as const]

export const ProductSearchRefinementCheckbox: FC<
  ProductSearchRefinementCheckboxProps
> = ({ attribute, hidden, transformItems }) => {
  const intl = useIntl()

  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const limit = isMobile
    ? PRODUCT_SEARCH_REFINEMENT_CHECKBOX_REDUCED_FACET_LIMIT
    : PRODUCT_SEARCH_REFINEMENT_CHECKBOX_FACET_LIMIT

  const refinementList = useRefinementList({
    attribute,
    limit,
    showMore: Boolean(isMobile),
    showMoreLimit: PRODUCT_SEARCH_REFINEMENT_CHECKBOX_FACET_LIMIT,
    sortBy: DEFAULT_SORT_BY,
    transformItems,
  })

  const contextValue = useMemo(
    () => ({
      refinementList,
      refinementListOptions: {
        attribute,
        limit,
        showMore: Boolean(isMobile),
        showMoreLimit: PRODUCT_SEARCH_REFINEMENT_CHECKBOX_FACET_LIMIT,
        sortBy: DEFAULT_SORT_BY,
        transformItems,
      },
    }),
    [attribute, limit, refinementList, transformItems, isMobile],
  )

  const { isFromSearch, items, refine } = refinementList

  const onChange: ProductSearchRefinementCheckboxProps['onChange'] = (
    event,
    selectedItems,
  ) => {
    for (const item of items) {
      if (shouldRefine(item, selectedItems)) {
        refine(item.value)
      }
    }
  }

  const value = useMemo(() => items.filter((item) => item.isRefined), [items])

  if (
    /**
     * Hidden widget visually but still execute Algolia hook to
     * preserve InstantSearch UI state and included in search requests.
     * More https://www.algolia.com/doc/guides/building-search-ui/widgets/customize-an-existing-widget/react/#building-a-virtual-widget-with-hooks
     */
    hidden ||
    (!isFromSearch && items.length === 0)
  ) {
    return null
  }

  const formLabel = productSearchGetRefinementLabel(attribute, intl)

  return (
    <ProductSearchRefinementContext value={contextValue}>
      <ProductSearchRefinement
        areOptionsEqual={areOptionsEqual}
        aria-label={formLabel}
        attribute={attribute}
        data-qa-id={`form-${formLabel}`}
        multiple
        onChange={onChange}
        renderValue={() => (
          <>
            <span className="truncate">{formLabel}</span>
            {value.length > 0 && (
              <span className="text-dark-primary-strong">{value.length}</span>
            )}
          </>
        )}
        slotProps={{
          listbox: {
            title: formLabel,
          },
        }}
        slots={{
          listbox: ProductSearchRefinementCheckboxListbox,
        }}
        value={value}
      >
        {items.map((item) => (
          <ProductSearchRefinementCheckboxOption
            data-attribute={attribute}
            item={item}
            key={item.label}
          />
        ))}
      </ProductSearchRefinement>
    </ProductSearchRefinementContext>
  )
}
